import React from 'react';
import { SubmissionError } from 'redux-form';
import { toPairs, noop, isNil, reduce, get, set, isEmpty } from 'lodash';

import LibTextField from '@material-ui/core/TextField';
import LibSelectField from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import LibCheckbox from '@material-ui/core/Checkbox';

import cx from 'classnames';
import { string, bool, number, oneOfType, objectOf } from 'prop-types';

import { PropTypes } from 'helpers/react';
const { children, inputShape, metaShape, any } = PropTypes;

import ImageUpload from 'shared-components/ImageUpload';
import styles from '../styles/index.scss';

export const TextField = ({
  input,
  className,
  label,
  hintText,
  type,
  multiLine,
  rows,
  rowsMax,
  maxLength,
  min,
  max,
  step,
  meta: { touched, error, warning },
  maxLengthLimit,
  disabled,
  variant,
}) => {
  const maxLengthLabel = `${label} (${input.value.length}/${maxLength})`;
  const errorData = touched && (error || warning);
  return (
    <LibTextField
      type={type}
      placeholder={hintText}
      error={errorData}
      multiline={multiLine}
      rows={rows}
      rowsMax={rowsMax}
      className={cx(className, styles.margin)}
      label={maxLength && input.value.length ? maxLengthLabel : label}
      helperText={errorData}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      inputProps={{
        maxLength: maxLengthLimit || null,
      }}
      variant={variant}
      {...input}
    />
  );
};

TextField.propTypes = {
  input: inputShape,
  meta: metaShape,
  label: string,
  hintText: string,
  type: string,
  className: string,
  multiLine: bool,
  autofocus: bool,
  rows: number,
  rowsMax: number,
  maxLength: number,
  min: number,
  max: number,
  step: oneOfType([number, string]),
  maxLengthLimit: number,
  disabled: bool,
  variant: string,
  inputProps: objectOf(any),
};

// blockFocusBlur hack:
// see https://github.com/callemall/material-ui/issues/5260
export const SelectField = ({
  input,
  label,
  children,
  blockFocusBlur,
  loading,
  loadingError,
  errorText = 'Failed to load menu items',
  meta: { touched, error },
  ...custom
}) => {
  const handleChange = (event) => {
    input.onChange(event, event.target.value);
  };

  const loadingLabelStyle = {
    right: '50%',
    // top: 25
  };
  const customData = { ...custom };
  // const _label = customData.floatingLabelText;
  delete customData.floatingLabelText;
  const _error = (loadingError && errorText) || (touched && error);

  return (
    <FormControl {...customData} error={_error}>
      <InputLabel style={(loading && loadingLabelStyle) || {}}>
        {loading ? <CircularProgress size={28} /> : label}
      </InputLabel>
      <LibSelectField
        disabled={loading || loadingError}
        {...input}
        onChange={handleChange}
        onFocus={blockFocusBlur ? noop : input.onFocus}
        onBlur={blockFocusBlur ? noop : input.onBlur}
        children={children}
      />
      {_error ? <FormHelperText>{_error}</FormHelperText> : ''}
    </FormControl>
  );
};

SelectField.propTypes = {
  input: inputShape,
  meta: metaShape,
  label: string,
  children,
  blockFocusBlur: bool,
  loading: bool,
  loadingError: bool,
  errorText: string,
};

export const IconSelectField = ({
  input,
  label,
  children,
  blockFocusBlur,
  loading,
  loadingError,
  errorText = 'Failed to load menu items',
  meta: { touched, error },
  ...custom
}) => {
  const handleChange = (event) => {
    input.onChange(event, event.target.value);
  };

  const handleRender = (value) => {
    const findingIndex = children.findIndex(
      (child) => child.props.value === value
    );
    return findingIndex !== -1
      ? children[findingIndex].props.children[1].props.children
      : '';
  };

  const loadingLabelStyle = {
    right: '50%',
    // top: 25
  };
  const customData = { ...custom };
  const _label = customData.floatingLabelText;
  delete customData.floatingLabelText;
  const _error = (loadingError && errorText) || (touched && error);

  return (
    <FormControl {...customData} error={_error}>
      <InputLabel style={(loading && loadingLabelStyle) || {}}>
        {loading ? <CircularProgress size={28} /> : _label}
      </InputLabel>
      <LibSelectField
        disabled={loading || loadingError}
        {...input}
        onChange={handleChange}
        onFocus={blockFocusBlur ? noop : input.onFocus}
        onBlur={blockFocusBlur ? noop : input.onBlur}
        children={children}
        renderValue={handleRender}
      />
      {_error ? <FormHelperText>{_error}</FormHelperText> : ''}
    </FormControl>
  );
};

IconSelectField.propTypes = {
  input: inputShape,
  meta: metaShape,
  label: string,
  children,
  blockFocusBlur: bool,
  loading: bool,
  loadingError: bool,
  errorText: string,
};

export const ImageUploadField = ({
  input: { value, onChange },
  meta,
  ...custom
}) => {
  const handleChange = ({ url }) => {
    console.log(url);
    onChange(url);
  };

  return (
    <div>
      <ImageUpload {...custom} onSuccess={handleChange} displayImage={value} />
    </div>
  );
};

ImageUploadField.propTypes = {
  input: inputShape,
  meta: metaShape,
  label: string,
  children,
  blockFocusBlur: bool,
};

// export const DropDownMenu = ({
//   input, className, children, blockFocusBlur
//   // meta: { touched, error, warning }
// }) => {
//   function handleChange(event, key, payload) {
//     return input.onChange(String(payload));
//   }
//
//   return (
//     <LibDropDownMenu className={ className }
//                      { ...input }
//                      onChange={ handleChange }
//                      onFocus={ blockFocusBlur ? noop : input.onFocus }
//                      onBlur={ blockFocusBlur ? noop : input.onBlur }
//                      underlineStyle={{ marginLeft: 0 }}
//     >
//       { children }
//     </LibDropDownMenu>
//   );
// };
//
// DropDownMenu.propTypes = {
//   children: children,
//   input: inputShape,
//   className: string,
//   blockFocusBlur: bool
// };

// based on https://github.com/erikras/redux-form-material-ui-legacy/blob/5.0/src/Checkbox.js
export const Checkbox = ({
  /* eslint-disable react/prop-types, react/jsx-no-bind */
  meta,
  input: { onChange, value, ...inputProps },
  label,
  className,
  ...props
}) => {
  const handleCheck = (value) => onChange(value);
  return (
    <FormControlLabel
      className={cx(className)}
      control={
        <LibCheckbox
          checked={value}
          onChange={handleCheck}
          {...inputProps}
          {...props}
        />
      }
      label={label}
    />
  );
};

export const syncValidation = (rules) => (formValues) => {
  return toPairs(rules).reduce((result, [name, rule]) => {
    const ruleResult = rule(formValues);

    if (ruleResult) {
      result[name] = ruleResult;
    }

    return result;
  }, {});
};

export const requireFields = (fields = [], { message = 'Required' } = {}) => (
  formValues
) => {
  return fields.reduce((result, field) => {
    if (isNil(get(formValues, field)) || get(formValues, field) === '') {
      set(result, field, message);
    }

    return result;
  }, {});
};

export const composeValidations = (validations) => (vals) =>
  reduce(validations, (r, fn) => ({ ...r, ...fn(vals) }), {});

export const handleGraphQLSubmissionFailure = (error) => {
  const validationErrors = get(error.errors, '[0].data.validationErrors', []);

  if (!isEmpty(validationErrors)) {
    throw new SubmissionError({
      ...validationErrors.reduce(
        (errorsMap, error) => ({
          ...errorsMap,
          ...set({}, error.fieldName, error.message),
        }),
        {}
      ),
    });
  }

  throw new SubmissionError({
    _error: "Failed to submit, but we can't tell why. Is your internet down?",
  });
};



// WEBPACK FOOTER //
// ./src/client/helpers/redux/form/index.js